import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { requirePermission } from "@/lib/permissions/permissions";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CalendarClock,
  ChevronsUpDown,
  Circle,
  GripVertical,
  Hash,
  Image as ImageIcon,
  MoreHorizontal,
  Plus,
  Slash,
  Square,
  Text,
  Trash2,
  Ruler,
  User,
  Info,
  Copy,
  ExternalLink,
  Rocket,
  Settings,
} from "lucide-react";
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import {
  ButtonGroup,
  ButtonGroupSeparator,
} from "@/components/ui/button-group";
import { Switch } from "@/components/ui/switch";
import { errorToast } from "@/lib/utils/ToastUtils";
import { cn } from "@/lib/utils/ui";
import { useNavbar } from "@/components/navbar";
import { useOrg } from "@/lib/states/session/hooks";
import CheckboxType from "@/components/forms/QuestionTypes/CheckboxType";
import RadioType from "@/components/forms/QuestionTypes/RadioType";
import LeadInfoType from "@/components/forms/QuestionTypes/LeadInfoType";
import { toast } from "sonner";
import LoadingSpinner from "@/components/LoadingSpinner";
import MetricType from "@/components/forms/QuestionTypes/MetricType";
import CalendlyType from "@/components/forms/QuestionTypes/CalendlyType";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import DeleteFormAlert from "@/components/forms/DeleteFormAlert";
import { Form } from "@/components/forms/formColumns";
import AlertBlocker from "@/components/AlertBlocker";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  MeasuringStrategy,
  type DraggableAttributes,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { v4 as uuidv4 } from "uuid";
import { useOrgEntitlements } from "@/lib/hooks/useOrgEntitlements";
import { canUseLeadForms, canUsePublicIdentifier } from "@/lib/billing/plans";
import { useDocumentTitle } from "@/lib/hooks/useDocumentTitle";
import { trpc } from "@/lib/trpc/client";
import { trpcUtils } from "@/lib/trpc/vanilla";
import { patchListCache, patchQueryCache, replaceInList } from "@trainrr/api-client";

type FormDetail = Awaited<ReturnType<typeof trpcUtils.forms.get.ensureData>>;

interface FormQuestion {
  title: string;
  description: string | null;
  type: string;
  data?: any;
  required: boolean;
  sortableId: string;
}

function toFormQuestions(form: FormDetail): FormQuestion[] {
  return [...form.questions]
    .sort((a, b) => (a.q_num ?? 0) - (b.q_num ?? 0))
    .map((question) => ({
      ...question,
      sortableId:
        question.q_num != null ? `question-${question.q_num}` : uuidv4(),
    }));
}

export const Route = createFileRoute("/_auth/_dashboard/forms/$formId")({
  beforeLoad: () => {
    requirePermission("forms.edit");
  },
  component: FormView,
  loader: ({ params }) =>
    trpcUtils.forms.get.ensureData({ formId: params.formId }),
});

function FormView() {
  const { formId } = Route.useParams();
  const { data: form } = trpc.forms.get.useQuery({ formId });
  if (!form) return <LoadingSpinner />;
  return <FormEditor key={form.id} form={form} />;
}

function FormEditor({ form }: { form: FormDetail }) {
  const { setContent } = useNavbar();
  const { formId } = Route.useParams();
  const org = useOrg();
  const { data: entitlements } = useOrgEntitlements();
  const allowPublicIdentifier = canUsePublicIdentifier(entitlements);
  const allowLeadForms = canUseLeadForms(entitlements);
  const navigate = useNavigate();
  const router = useRouter();
  const utils = trpc.useUtils();
  const [questions, setQuestions] = useState<FormQuestion[]>(() =>
    toFormQuestions(form),
  );
  const [formName, setFormName] = useState(form.name);
  const [formSlug, setFormSlug] = useState(form.slug ?? "");
  const [slugError, setSlugError] = useState<string | null>(null);
  const [formToDelete, setFormToDelete] = useState<Form>();
  const [hasChanges, setHasChanges] = useState(false);

  useDocumentTitle(formName || "Form");

  const [saving, setSaving] = useState(false);
  const updateForm = trpc.forms.update.useMutation();

  if (form.type === "lead" && !allowLeadForms) {
    return (
      <div className="w-full p-8">
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Lead forms require Growth</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              This lead form is restricted on your current plan. Upgrade to
              Growth or above to edit and publish lead forms.
            </p>
            <Button asChild size="sm">
              <Link to="/upgrade" search={{ plan: "growth_monthly" }}>
                View upgrade options
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const breadcrumb = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to="/forms">Forms</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <Slash />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{formName}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    [formName],
  );

  const publicUrl = useMemo(() => {
    if (form.type !== "lead") return "";
    const base = "https://forms.trainrr.app";
    const orgRef = allowPublicIdentifier && org?.slug ? org.slug : form.org_id;
    const slug = formSlug.trim();
    if (!slug) return "";
    return `${base}/${orgRef}/${slug}`;
  }, [allowPublicIdentifier, form.org_id, form.type, formSlug, org?.slug]);

  useEffect(() => {
    setContent(breadcrumb);
    return () => setContent(null);
  }, [breadcrumb, setContent]);

  useEffect(() => {
    if (form.type !== "lead") return;
    if (formSlug.trim()) return;
    if (!formName.trim()) return;
    setFormSlug(slugify(formName));
    setHasChanges(true);
  }, [form.type, formName, formSlug]);

  const saveForm = async () => {
    if (form.type === "lead" && !formSlug.trim()) {
      setSlugError("Add a unique public URL before saving.");
      toast.error("Public URL is required to save this lead form.");
      return;
    }
    setSaving(true);
    const publishValue =
      form.type === "lead" ? true : (form.is_published ?? false);
    const q = questions.map((q, i) => {
      return {
        q_num: i,
        type: q.type,
        title: q.title,
        description: q.description,
        data: q.data,
        required: q.required,
      };
    });
    const params = {
      form_id: formId,
      questions: q,
      form_name: formName,
      form_slug: form.type === "lead" ? formSlug.trim() : undefined,
      is_published: form.type === "lead" ? publishValue : undefined,
    };
    try {
      await updateForm.mutateAsync({ params });
      patchQueryCache(utils.forms.get, { formId }, (old) => ({
        ...old,
        name: formName,
        notes: form.notes,
        slug: formSlug.trim() || null,
        is_published: publishValue,
        questions: q,
      }));
      patchListCache(utils.forms.list, (old) =>
        replaceInList(old, {
          id: form.id,
          org_id: form.org_id,
          name: formName,
          notes: form.notes,
          created_at: form.created_at,
          type: form.type,
          slug: formSlug.trim() || null,
          is_published: publishValue,
        }),
      );
      void utils.forms.listOptions.invalidate();
      await router.invalidate();
      setHasChanges(false);
      setSlugError(null);
      toast.success("Form saved");
    } catch (error) {
      console.error("Error updating form", error);
      const message = error instanceof Error ? error.message : "";
      if (message.includes("forms_org_slug_key")) {
        setSlugError(
          "That public URL is already in use with another form. Try another one.",
        );
        toast.error(
          "Public URL already in use with another form. Try another one.",
        );
      } else {
        errorToast(error instanceof Error ? error : null);
      }
    }
    setSaving(false);
  };

  const onQuestionChange = (index: number, question: FormQuestion) => {
    const newQuestions = [...questions];
    newQuestions[index] = question;
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  const deleteQuestion = (index: number) => {
    const newQuestions = [...questions];
    newQuestions.splice(index, 1);
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  const addQuestion = (type: string) => {
    const typeDefaults = getQuestionTypeDefaults(type);
    setQuestions([
      ...questions,
      {
        title: "",
        description: "",
        type,
        data: typeDefaults.data,
        required: typeDefaults.required ?? false,
        sortableId: uuidv4(),
      },
    ]);
    setHasChanges(true);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    setQuestions((current) => {
      const oldIndex = current.findIndex((q) => q.sortableId === active.id);
      const newIndex = current.findIndex((q) => q.sortableId === over.id);
      if (oldIndex === newIndex) {
        return current;
      }
      setHasChanges(true);
      return arrayMove(current, oldIndex, newIndex);
    });
  };

  return (
    <>
      <AlertBlocker shouldBlock={hasChanges && !saving} />
      <DeleteFormAlert
        form={formToDelete}
        onOpenChange={(open) => {
          if (!open) {
            setFormToDelete(undefined);
          }
        }}
        onDeleted={() => {
          setHasChanges(false);
          navigate({ to: "/forms" });
        }}
      />
      <div className="mx-auto w-full max-w-4xl px-4 py-6 pb-24 md:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Input
            value={formName}
            onChange={(e) => {
              setFormName(e.target.value);
              setHasChanges(true);
            }}
            placeholder="New Form"
            className="border-0 bg-transparent pl-2 pr-0 text-2xl font-semibold shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <ButtonGroup className="w-full sm:w-auto">
            <Button
              onClick={saveForm}
              disabled={saving || (!hasChanges && form.is_published)}
              variant={"outline"}
              className={form.type === "lead" ? "gap-2" : undefined}
            >
              {form.type === "lead" ? (
                <>
                  <Rocket className="h-4 w-4" />
                  {hasChanges
                    ? "Save & Publish"
                    : form.is_published
                      ? "Published"
                      : "Publish"}
                </>
              ) : (
                "Save"
              )}
              {saving && <LoadingSpinner className="ml-2" />}
            </Button>
            <ButtonGroupSeparator />
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {form.type === "lead" ? (
                  <>
                    <DropdownMenuItem
                      disabled={!publicUrl}
                      onClick={() => {
                        if (!publicUrl) {
                          toast.error("Add a public slug to enable the link.");
                          return;
                        }
                        navigator.clipboard.writeText(publicUrl);
                        toast.success("Public URL copied");
                      }}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Copy URL
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={!publicUrl}
                      onClick={() => {
                        if (!publicUrl) {
                          toast.error("Add a public slug to enable the link.");
                          return;
                        }
                        window.open(publicUrl, "_blank", "noopener,noreferrer");
                      }}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Preview
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : null}
                <DropdownMenuItem
                  onClick={() =>
                    setFormToDelete({
                      id: form.id,
                      org_id: form.org_id,
                      name: form.name,
                      notes: form.notes,
                      created_at: form.created_at,
                      type: form.type,
                    })
                  }
                  variant="destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Form
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        </div>

        <Separator className="mt-6" />
        {form.type === "lead" ? (
          <>
            <div className="mt-6 rounded-2xl border border-border bg-background p-4">
              <h3 className="text-sm font-semibold text-foreground">
                Lead form settings
              </h3>
              <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Public URL</p>
                  <Input
                    value={formSlug}
                    onChange={(e) => {
                      setFormSlug(slugify(e.target.value));
                      setSlugError(null);
                      setHasChanges(true);
                    }}
                    placeholder="new-client"
                  />
                  {slugError ? (
                    <p className="text-xs text-destructive">{slugError}</p>
                  ) : null}
                  <div className="group inline-flex items-center gap-2">
                    <button
                      type="button"
                      className="text-xs text-secondary-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed"
                      disabled={!publicUrl}
                      onClick={() => {
                        if (!publicUrl) {
                          toast.error("Add a public slug to enable the link.");
                          return;
                        }
                        navigator.clipboard.writeText(publicUrl);
                        toast.success("Public URL copied");
                      }}
                    >
                      Public URL: forms.trainrr.app/
                      {allowPublicIdentifier && org?.slug
                        ? org.slug
                        : form.org_id}
                      /{formSlug || "your-slug"}
                    </button>
                    <Button
                      size={"icon"}
                      variant={"ghost"}
                      className="size-7 items-center justify-center text-secondary-foreground"
                      disabled={!publicUrl}
                      onClick={() => {
                        if (!publicUrl) {
                          toast.error("Add a public slug to enable the link.");
                          return;
                        }
                        navigator.clipboard.writeText(publicUrl);
                        toast.success("Public URL copied");
                      }}
                    >
                      <Copy />
                    </Button>
                    <Button
                      size={"icon"}
                      variant={"ghost"}
                      className="size-7 items-center justify-center text-secondary-foreground"
                      disabled={!publicUrl}
                      onClick={() => {
                        if (!publicUrl) {
                          toast.error("Add a public slug to enable the link.");
                          return;
                        }
                        window.open(publicUrl, "_blank", "noopener,noreferrer");
                      }}
                    >
                      <ExternalLink />
                    </Button>
                  </div>
                  {!allowPublicIdentifier ? (
                    <Alert variant="info" className="mt-2 text-xs">
                      <Info className="h-4 w-4" />
                      <AlertTitle className="flex items-start justify-between gap-3 font-medium">
                        <span>Upgrade to use a public identifier</span>
                        <Button
                          size={"xs"}
                          onClick={() =>
                            navigate({ to: "/settings/organisation" })
                          }
                        >
                          <Settings className="inline h-4 w-4" /> View Now
                        </Button>
                      </AlertTitle>
                      <AlertDescription className="flex items-center gap-2 text-xs text-primary">
                        Upgrade to Growth or Team to use a custom identifier in
                        public URLs.
                      </AlertDescription>
                    </Alert>
                  ) : null}
                </div>
              </div>
            </div>
            <Separator className="mt-6" />
          </>
        ) : null}
        <div className="mt-6 flex flex-col gap-6">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          >
            <SortableContext
              items={questions.map((q) => q.sortableId)}
              strategy={verticalListSortingStrategy}
            >
              {questions.map((q, i) => (
                <SortableQuestion
                  key={q.sortableId}
                  index={i}
                  question={q}
                  formType={form.type}
                  onQuestionChange={onQuestionChange}
                  deleteQuestion={deleteQuestion}
                />
              ))}
            </SortableContext>
          </DndContext>
          <AddQuestionTypeDropdown
            formType={form.type}
            onTypeSelected={addQuestion}
          />
        </div>
      </div>
    </>
  );
}

interface FormQuestionProps {
  index: number;
  question: FormQuestion;
  formType?: string;
  onQuestionChange: (index: number, question: FormQuestion) => void;
  deleteQuestion: (index: number) => void;
  dragHandleProps?: {
    attributes?: DraggableAttributes;
    listeners?: ReturnType<typeof useSortable>["listeners"];
  };
}

const FormQuestion: React.FC<FormQuestionProps> = ({
  question,
  index,
  formType,
  onQuestionChange,
  deleteQuestion,
  dragHandleProps,
}) => {
  const handleTypeChange = (value: string) => {
    onQuestionChange(index, {
      ...question,
      ...getQuestionTypeDefaults(value),
      type: value,
    });
  };

  const dataChange = (data: any) => {
    onQuestionChange(index, { ...question, data });
  };

  const titleRef = useRef<HTMLTextAreaElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    [titleRef.current, descriptionRef.current].forEach((element) => {
      if (!element) return;
      element.style.height = "0px";
      element.style.height = `${element.scrollHeight}px`;
    });
  }, [question.title, question.description]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col items-stretch justify-between gap-4 space-y-0 px-4 pb-5 pt-4 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <div
            className="mt-5 h-6 w-6 cursor-grab rounded-md p-1 text-muted-foreground touch-none active:cursor-grabbing"
            aria-label="Reorder question"
            {...dragHandleProps?.attributes}
            {...dragHandleProps?.listeners}
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <FormQuestionTypeSelect
            onTypeSelected={handleTypeChange}
            value={question.type}
            formType={formType}
            variant="header"
          />
        </div>
        <div className="flex shrink-0 items-center justify-end gap-3 pt-3">
          {question.type !== "lead_info" ? (
            <>
              <span className="text-sm font-medium text-muted-foreground">
                Required
              </span>
              <Switch
                size="sm"
                checked={question.required}
                onCheckedChange={(value) =>
                  onQuestionChange(index, { ...question, required: value })
                }
              />
            </>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-10 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/15 hover:text-destructive"
            onClick={() => deleteQuestion(index)}
            aria-label="Remove question"
          >
            <Trash2 className="h-5 w-5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Textarea
            ref={titleRef}
            placeholder="Type your question here..."
            value={question.title}
            onChange={(e) =>
              onQuestionChange(index, {
                ...question,
                title: e.target.value,
              })
            }
            aria-label="Question title"
            className="min-h-8 resize-none overflow-hidden border-0 bg-transparent px-0 py-0 text-xl font-semibold leading-snug shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0 md:text-2xl"
          />
          <Textarea
            ref={descriptionRef}
            placeholder="Add a description (optional)"
            value={question.description ?? ""}
            onChange={(e) =>
              onQuestionChange(index, {
                ...question,
                description: e.target.value,
              })
            }
            aria-label="Question description"
            className="min-h-6 resize-none overflow-hidden border-0 bg-transparent px-0 py-0 text-base leading-snug shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        {question.type === "checkbox" && (
          <CheckboxType data={question.data} onDataChanged={dataChange} />
        )}
        {question.type === "radio" && (
          <RadioType data={question.data} onDataChanged={dataChange} />
        )}
        {question.type === "metric" && (
          <MetricType data={question.data} onDataChanged={dataChange} />
        )}
        {question.type === "lead_info" && (
          <LeadInfoType data={question.data} onDataChanged={dataChange} />
        )}
        {question.type === "calendly" && (
          <CalendlyType data={question.data} onDataChanged={dataChange} />
        )}
      </CardContent>
    </Card>
  );
};

const SortableQuestion: React.FC<FormQuestionProps> = (props) => {
  const { question } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.sortableId });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, zIndex: isDragging ? 10 : undefined }}
      className="w-full"
    >
      <FormQuestion {...props} dragHandleProps={{ attributes, listeners }} />
    </div>
  );
};

interface FormQuestionType {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  colorClassName: string;
}

const coreQuestionTypes: FormQuestionType[] = [
  {
    id: "text",
    name: "Text",
    description: "Freeform text response",
    icon: <Text className="h-4 w-4" />,
    colorClassName: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  },
  {
    id: "checkbox",
    name: "Multiselect",
    description: "Allow multiple selections",
    icon: <Square className="h-4 w-4" />,
    colorClassName:
      "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  },
  {
    id: "radio",
    name: "Select",
    description: "Choose one option",
    icon: <Circle className="h-4 w-4" />,
    colorClassName:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  },
  {
    id: "number",
    name: "Number",
    description: "Numeric entry only",
    icon: <Hash className="h-4 w-4" />,
    colorClassName:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  },
  {
    id: "image",
    name: "Image",
    description: "Upload photos",
    icon: <ImageIcon className="h-4 w-4" />,
    colorClassName:
      "bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300",
  },
  {
    id: "metric",
    name: "Metric",
    description: "Automatically log this metric when the form is submitted",
    icon: <Ruler className="h-4 w-4" />,
    colorClassName:
      "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  },
  {
    id: "lead_info",
    name: "Contact info",
    description: "Capture contact details",
    icon: <User className="h-4 w-4" />,
    colorClassName:
      "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300",
  },
];

const integrationQuestionTypes: FormQuestionType[] = [
  {
    id: "calendly",
    name: "Calendly",
    description: "Embed a Calendly booking step",
    icon: <CalendarClock className="h-4 w-4" />,
    colorClassName:
      "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
  },
];

function getSelectableQuestionTypes(formType?: string) {
  const coreTypes = coreQuestionTypes.filter((type) => {
    if (formType === "lead") {
      return type.id !== "image" && type.id !== "metric";
    }
    return type.id !== "lead_info";
  });

  return {
    coreTypes,
    integrationTypes: formType === "lead" ? integrationQuestionTypes : [],
  };
}

function getQuestionTypeDefaults(type: string): Partial<FormQuestion> {
  if (type === "checkbox" || type === "radio") {
    return { data: { options: ["", ""] } };
  }

  if (type === "lead_info") {
    return {
      data: {
        fields: {
          first_name: { enabled: true, required: false },
          last_name: { enabled: true, required: false },
          email: { enabled: true, required: false },
          phone: { enabled: true, required: false },
        },
      },
    };
  }

  if (type === "calendly") {
    return { data: { url: "" }, required: true };
  }

  return { data: undefined };
}

interface FormQuestionTypeSelectProps {
  onTypeSelected: (value: string) => void;
  value?: string;
  formType?: string;
  variant?: "default" | "header";
}

const FormQuestionTypeSelect: FunctionComponent<
  FormQuestionTypeSelectProps
> = ({ value, onTypeSelected, formType, variant = "default" }) => {
  const {
    coreTypes: selectableCoreTypes,
    integrationTypes: selectableIntegrationTypes,
  } = getSelectableQuestionTypes(formType);
  const selectedType = [...coreQuestionTypes, ...integrationQuestionTypes].find(
    (t) => t.id === value,
  );

  return (
    <Select value={value} onValueChange={onTypeSelected}>
      <SelectTrigger
        className={cn(
          "h-auto w-full items-start [&>span]:line-clamp-none",
          variant === "header" &&
            "group relative max-w-xl justify-start gap-3 rounded-lg border-none bg-transparent px-2 py-2 text-left shadow-none ring-offset-0 transition-colors hover:bg-muted/50 focus:ring-0 focus:ring-offset-0 data-[state=open]:bg-muted/50 [&>svg]:hidden",
        )}
      >
        <SelectValue placeholder="Select question type" className="w-full">
          {selectedType && (
            <span className="flex w-full items-start gap-3 pr-8 text-left">
              <span
                className={cn(
                  "mt-0.5 text-muted-foreground",
                  variant === "header" &&
                    "flex size-12 items-center justify-center rounded-xl [&>svg]:h-5 [&>svg]:w-5",
                  variant === "header" && selectedType.colorClassName,
                )}
              >
                {selectedType.icon}
              </span>
              <span className="flex flex-col">
                <span
                  className={cn(
                    "text-sm",
                    variant === "header" &&
                      "text-lg font-semibold text-foreground",
                  )}
                >
                  {selectedType.name}
                </span>
                <span
                  className={cn(
                    "text-xs text-muted-foreground",
                    variant === "header" && "text-sm",
                  )}
                >
                  {selectedType.description}
                </span>
              </span>
            </span>
          )}
        </SelectValue>
        {variant === "header" ? (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-70 group-data-[state=open]:opacity-70">
            <ChevronsUpDown className="h-4 w-4" />
          </span>
        ) : null}
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {selectableIntegrationTypes.length ? (
            <SelectLabel>Questions</SelectLabel>
          ) : null}
          {selectableCoreTypes.map((t) => (
            <SelectItem key={t.id} value={t.id}>
              <span className="flex items-center gap-3">
                <span
                  className={cn(
                    "flex size-8 items-center justify-center rounded-lg [&>svg]:h-4 [&>svg]:w-4",
                    t.colorClassName,
                  )}
                >
                  {t.icon}
                </span>
                <span className="flex flex-col">
                  <span className="text-sm">{t.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {t.description}
                  </span>
                </span>
              </span>
            </SelectItem>
          ))}
          {selectableIntegrationTypes.length ? (
            <>
              <SelectSeparator />
              <SelectLabel>Integrations</SelectLabel>
              {selectableIntegrationTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  <span className="flex items-center gap-3">
                    <span
                      className={cn(
                        "flex size-8 items-center justify-center rounded-lg [&>svg]:h-4 [&>svg]:w-4",
                        t.colorClassName,
                      )}
                    >
                      {t.icon}
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm">{t.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {t.description}
                      </span>
                    </span>
                  </span>
                </SelectItem>
              ))}
            </>
          ) : null}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

interface AddQuestionTypeDropdownProps {
  formType?: string;
  onTypeSelected: (value: string) => void;
}

const AddQuestionTypeDropdown: FunctionComponent<
  AddQuestionTypeDropdownProps
> = ({ formType, onTypeSelected }) => {
  const { coreTypes, integrationTypes } = getSelectableQuestionTypes(formType);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="mt-2 h-14 w-full border-dashed text-muted-foreground hover:text-foreground"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Question
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[min(28rem,calc(100vw-2rem))]">
        {integrationTypes.length ? (
          <DropdownMenuLabel>Questions</DropdownMenuLabel>
        ) : null}
        {coreTypes.map((type) => (
          <DropdownMenuItem
            key={type.id}
            className="py-2"
            onSelect={() => onTypeSelected(type.id)}
          >
            <QuestionTypeMenuItem type={type} />
          </DropdownMenuItem>
        ))}
        {integrationTypes.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Integrations</DropdownMenuLabel>
            {integrationTypes.map((type) => (
              <DropdownMenuItem
                key={type.id}
                className="py-2"
                onSelect={() => onTypeSelected(type.id)}
              >
                <QuestionTypeMenuItem type={type} />
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface QuestionTypeMenuItemProps {
  type: FormQuestionType;
}

const QuestionTypeMenuItem: FunctionComponent<QuestionTypeMenuItemProps> = ({
  type,
}) => {
  return (
    <span className="flex items-center gap-3">
      <span
        className={cn(
          "flex size-8 items-center justify-center rounded-lg [&>svg]:h-4 [&>svg]:w-4",
          type.colorClassName,
        )}
      >
        {type.icon}
      </span>
      <span className="flex flex-col">
        <span className="text-sm">{type.name}</span>
        <span className="text-xs text-muted-foreground">
          {type.description}
        </span>
      </span>
    </span>
  );
};

export default FormView;

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}
